import Tooltip from '$components/Tooltip'
import { Button, Card, Col, Row, notification, Modal, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchAllMaterials as fetchDwhMaterialsAPI,
  mapDwhMaterialsToOperatorAdditiveMaterial,
  postDwhMaterial,
} from '$api/evoAPIs'
import { useMemo, useState } from 'react'
import MaterialInfo from './MaterialInfo'
import OperatorMaterials from './OperatorMaterials'
import AddOperatorAdditiveMaterial from './AddOperatorAdditiveMaterial'
import DwhMaterialsTable from './DwhMaterialsTable'
import { PlusOutlined } from '@ant-design/icons'
import { theme } from 'antd'

const { useToken } = theme

const MaterialMapper = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { token } = useToken()

  const [listActionTrigger, setListActionTrigger] = useState(0)
  const [selectedDwhMaterialIds, setSelectedDwhMaterialIds] = useState(
    new Set(),
  )
  const [highlightedMaterialId, setHighlightedMaterialId] = useState(null)
  const [
    selectedOperatorAdditiveMaterial,
    setSelectedOperatorAdditiveMaterial,
  ] = useState(null)
  const [dwhMaterialClickedForInfo, setDwhMaterialClickedForInfo] =
    useState(null)

  const [isAddDwhModalVisible, setIsAddDwhModalVisible] = useState(false)
  const [newDwhMaterialName, setNewDwhMaterialName] = useState('')
  const [newDwhMaterialDescription, setNewDwhMaterialDescription] = useState('')

  const mapMaterialsApiMutation = useMutation(
    mapDwhMaterialsToOperatorAdditiveMaterial,
    {
      onSuccess: (updatedOperatorAdditiveMaterial) => {
        notification.success({ message: t('mapping_successful_message') })

        const newMappedIds = (
          updatedOperatorAdditiveMaterial.mapped_dwh_material_ids || []
        ).map((id) => id.toString())

        const newMappedIdsSet = new Set(newMappedIds)
        setSelectedDwhMaterialIds(newMappedIdsSet)

        queryClient.invalidateQueries({ queryKey: [] })
        setListActionTrigger((prev) => prev + 1)
      },
      onError: (_error) => {
        notification.error({ message: t('mapping_failed_message') })
      },
    },
  )

  const addDwhMaterialMutation = useMutation(postDwhMaterial, {
    onSuccess: (data) => {
      notification.success({
        message: t('material_added_successfully', { name: data.name }),
      })
      queryClient.invalidateQueries(['dwhMaterialsListForMapperPage'])
      setIsAddDwhModalVisible(false)
      setNewDwhMaterialName('')
      setNewDwhMaterialDescription('')
    },
    onError: (_error) => {
      notification.error({ message: t('error_adding_material') })
    },
  })

  const showUnmapButton = useMemo(() => {
    if (
      !selectedOperatorAdditiveMaterial ||
      selectedDwhMaterialIds.size === 0
    ) {
      return false
    }
    const originalMappedIds = new Set(
      (selectedOperatorAdditiveMaterial.mapped_dwh_material_ids || []).map(
        String,
      ),
    )
    for (const id of selectedDwhMaterialIds) {
      if (originalMappedIds.has(id)) {
        return true
      }
    }
    return false
  }, [selectedOperatorAdditiveMaterial, selectedDwhMaterialIds])

  const { data: dwhMaterialsPageData, isLoading: isLoadingDwhMaterials } =
    useQuery(
      ['dwhMaterialsListForMapperPage'],
      async () => {
        let allMaterials = []
        let currentPage = 1
        let hasMorePages = true

        while (hasMorePages) {
          const pageData = await fetchDwhMaterialsAPI({
            queryKey: [
              'dwhMaterialsListForMapperPage',
              { page: currentPage, size: 100 },
            ],
          })

          if (pageData.items && pageData.items.length > 0) {
            allMaterials = [...allMaterials, ...pageData.items]

            const totalFetched = currentPage * 100
            hasMorePages = totalFetched < pageData.total
            currentPage++
          } else {
            hasMorePages = false
          }
        }

        const firstPageData = await fetchDwhMaterialsAPI({
          queryKey: ['dwhMaterialsListForMapperPage', { page: 1, size: 100 }],
        })

        return {
          ...firstPageData,
          items: allMaterials,
          size: allMaterials.length,
        }
      },
      {
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
    )

  const dwhMaterialNameMap = useMemo(() => {
    if (!dwhMaterialsPageData || !dwhMaterialsPageData.items) return {}
    const nameMap = dwhMaterialsPageData.items.reduce((acc, material) => {
      acc[material.id.toString()] = material.name
      return acc
    }, {})
    return nameMap
  }, [dwhMaterialsPageData])

  const handleAdditiveMaterialSelect = (_materialId, materialObject) => {
    setSelectedOperatorAdditiveMaterial(materialObject)
    let existingMappings = new Set()
    if (materialObject?.mapped_dwh_material_ids) {
      const mappings = Array.isArray(materialObject.mapped_dwh_material_ids)
        ? materialObject.mapped_dwh_material_ids
        : []
      existingMappings = new Set(mappings.map((id) => id.toString()))
    }
    setSelectedDwhMaterialIds(existingMappings)
    setDwhMaterialClickedForInfo(null)
  }

  const mapButtonText =
    selectedDwhMaterialIds.size > 0
      ? `${t('map_material_button')} (${selectedDwhMaterialIds.size})`
      : t('map_material_button')

  const handleMapClick = () => {
    if (!selectedOperatorAdditiveMaterial) {
      notification.warning({ message: t('select_additive_material_first') })
      return
    }
    mapMaterialsApiMutation.mutate({
      operatorAdditiveMaterialId: selectedOperatorAdditiveMaterial.id,
      dwhMaterialIds: selectedDwhMaterialIds,
    })
  }

  const handleUnmapClick = () => {
    if (!selectedOperatorAdditiveMaterial) return

    const originalMappedIds = new Set(
      (selectedOperatorAdditiveMaterial.mapped_dwh_material_ids || []).map(
        String,
      ),
    )

    const idsToUnmap = selectedDwhMaterialIds
    idsToUnmap.forEach((id) => originalMappedIds.delete(id))

    mapMaterialsApiMutation.mutate({
      operatorAdditiveMaterialId: selectedOperatorAdditiveMaterial.id,
      dwhMaterialIds: originalMappedIds,
    })
  }

  const handleDwhMaterialSelectionChange = (newSelectedIdsSet) => {
    setSelectedDwhMaterialIds(newSelectedIdsSet)
  }

  const handleDwhMaterialRowClickForInfo = (dwhMaterialRecord) => {
    setDwhMaterialClickedForInfo(dwhMaterialRecord)
  }

  const handleOpenAddDwhModal = () => {
    setIsAddDwhModalVisible(true)
  }

  const handleAddDwhModalOk = () => {
    if (!newDwhMaterialName.trim()) {
      notification.error({ message: t('material_name_required') })
      return
    }
    addDwhMaterialMutation.mutate({
      name: newDwhMaterialName,
      description: newDwhMaterialDescription,
    })
  }

  const handleAddDwhModalCancel = () => {
    setIsAddDwhModalVisible(false)
    setNewDwhMaterialName('')
    setNewDwhMaterialDescription('')
  }

  return (
    <div style={{ padding: '24px 0' }}>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={10}>
          <Card
            title={
              <AddOperatorAdditiveMaterial
                setHighlightedMaterialId={setHighlightedMaterialId}
                setListActionTrigger={setListActionTrigger}
              />
            }
            style={{ height: '100%' }}
          >
            <OperatorMaterials
              onMaterialSelect={handleAdditiveMaterialSelect}
              selectedMaterialIdForMapping={
                selectedOperatorAdditiveMaterial?.id
              }
              highlightedMaterialId={highlightedMaterialId}
              onHighlightComplete={() => setHighlightedMaterialId(null)}
              dwhMaterialNameMap={dwhMaterialNameMap}
              selectedDwhMaterialIds={selectedDwhMaterialIds}
              listActionTrigger={listActionTrigger}
            />
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Row gutter={[16, 16]} style={{ height: '100%' }}>
            <Col xs={24} xl={16}>
              <Card
                title={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>{t('dwh_materials_header')}</span>
                    <Button
                      icon={<PlusOutlined />}
                      size="small"
                      onClick={handleOpenAddDwhModal}
                      style={{
                        borderRadius: '5px 10px 10px 5px',
                        borderColor: token.colorPrimary,
                        color: token.colorPrimary,
                      }}
                    >
                      {t('add_button')}
                    </Button>
                  </div>
                }
                style={{ height: '100%' }}
              >
                <DwhMaterialsTable
                  onSelectionChange={handleDwhMaterialSelectionChange}
                  selectedDwhKeysFromParent={selectedDwhMaterialIds}
                  dwhMaterialsData={dwhMaterialsPageData}
                  isLoadingDwhMaterials={isLoadingDwhMaterials}
                  onRowClick={handleDwhMaterialRowClickForInfo}
                />
              </Card>
            </Col>
            <Col xs={24} xl={8}>
              <Card
                title={
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    {t('material_info_title')}
                  </span>
                }
                style={{
                  height: '500px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                bodyStyle={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  padding: 0,
                }}
              >
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                  <MaterialInfo
                    selectedDwhMaterialForInfo={dwhMaterialClickedForInfo}
                    selectedDWHMaterialIds={selectedDwhMaterialIds}
                    dwhMaterialsData={dwhMaterialsPageData?.items || []}
                  />
                </div>
                <div
                  style={{
                    padding: '16px 24px',
                    borderTop: '1px solid #f0f0f0',
                    backgroundColor: '#fafafa',
                  }}
                >
                  {/* Footer actions go here */}
                  <Button
                    type="primary"
                    onClick={handleMapClick}
                    disabled={!selectedOperatorAdditiveMaterial}
                    style={{ width: '100%' }}
                    size="medium"
                  >
                    {mapButtonText}
                  </Button>
                  {showUnmapButton && (
                    <Tooltip
                      title={t(
                        'unmap_tooltip',
                        t('my_materials.material_mapper_toolip'),
                      )}
                    >
                      <Button
                        type="primary"
                        danger
                        onClick={handleUnmapClick}
                        disabled={!selectedOperatorAdditiveMaterial}
                        style={{ width: '100%', marginTop: '1vh' }}
                        size="medium"
                      >
                        {t('unmap_button', 'Unmap Selected')}
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      <Modal
        title={t(
          'dwh_materials.add_material_title',
          'Add New Conventional Material',
        )}
        open={isAddDwhModalVisible}
        onOk={handleAddDwhModalOk}
        onCancel={handleAddDwhModalCancel}
        okText={t('add_button')}
        cancelText={t('cancel_button')}
        confirmLoading={addDwhMaterialMutation.isLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input
            placeholder={t(
              'dwh_materials.material_name_placeholder',
              'Material Name',
            )}
            value={newDwhMaterialName}
            onChange={(e) => setNewDwhMaterialName(e.target.value)}
          />
          <Input.TextArea
            placeholder={t(
              'dwh_materials.material_description_placeholder',
              'Description',
            )}
            value={newDwhMaterialDescription}
            onChange={(e) => setNewDwhMaterialDescription(e.target.value)}
            rows={4}
          />
        </div>
      </Modal>
    </div>
  )
}

export default MaterialMapper
